import { lazy, Suspense } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { TooltipProvider } from "@/components/ui/tooltip";
import { LangProvider } from "@/i18n/LangContext";
import { CookieBanner } from "@/components/site/CookieBanner";
import { GoogleTagManager } from "@/components/site/GoogleTagManager";
import { SeoHeadLinks } from "@/components/site/SeoHeadLinks";
import { SiteSchema } from "@/components/site/SiteSchema";
import { ScrollToTop } from "@/components/ScrollToTop";
import Index from "./pages/Index.tsx";

const Toaster = lazy(() =>
  import("@/components/ui/toaster").then((m) => ({ default: m.Toaster })),
);
const Sonner = lazy(() =>
  import("@/components/ui/sonner").then((m) => ({ default: m.Toaster })),
);
const FindCertificateFab = lazy(() =>
  import("@/components/site/FindCertificateFab").then((m) => ({ default: m.FindCertificateFab })),
);

const NotFound = lazy(() => import("./pages/NotFound.tsx"));
const Lakarintyg = lazy(() => import("./pages/Lakarintyg.tsx"));
const Kontakt = lazy(() => import("./pages/Kontakt.tsx"));
const Boka = lazy(() => import("./pages/Boka.tsx"));
const Faq = lazy(() => import("./pages/Faq.tsx"));
const InforBesoket = lazy(() => import("./pages/InforBesoket.tsx"));
const SaFungerarDet = lazy(() => import("./pages/SaFungerarDet.tsx"));
const Guider = lazy(() => import("./pages/Guider.tsx"));
const Foretag = lazy(() => import("./pages/Foretag.tsx"));
const ForetagArbetsmiljo = lazy(() => import("./pages/ForetagArbetsmiljo.tsx"));
const ForetagDrogtestning = lazy(() => import("./pages/ForetagDrogtestning.tsx"));
const ForetagTjanster = lazy(() => import("./pages/ForetagTjanster.tsx"));
const ForetagKontakt = lazy(() => import("./pages/ForetagKontakt.tsx"));
const ForetagBokaTid = lazy(() => import("./pages/ForetagBokaTid.tsx"));
const Halsokontroller = lazy(() => import("./pages/Halsokontroller.tsx"));
const CertificateDetail = lazy(() => import("./pages/CertificateDetail.tsx"));
const OmOss = lazy(() => import("./pages/OmOss.tsx"));
const Bekraftelse = lazy(() => import("./pages/Bekraftelse.tsx"));
const StartaArende = lazy(() => import("./pages/StartaArende.tsx"));
const Drogtestning = lazy(() => import("./pages/Drogtestning.tsx"));
const DrogtestningPrivat = lazy(() => import("./pages/DrogtestningPrivat.tsx"));
const DrogtestningForIntyg = lazy(() => import("./pages/DrogtestningForIntyg.tsx"));
const DrogtestningBoka = lazy(() => import("./pages/DrogtestningBoka.tsx"));
const DrogtestningForfragan = lazy(() => import("./pages/DrogtestningForfragan.tsx"));
const Provtagning = lazy(() => import("./pages/Provtagning.tsx"));
const GuideDetail = lazy(() => import("./pages/GuideDetail.tsx"));
const LegalDocument = lazy(() => import("./pages/LegalDocument.tsx"));

const App = () => {
  return (
    <TooltipProvider>
      <Suspense fallback={null}>
        <Toaster />
        <Sonner />
      </Suspense>
      <BrowserRouter>
        <ScrollToTop />
        <LangProvider>
          <GoogleTagManager />
          <SeoHeadLinks />
          <SiteSchema />
          <Suspense fallback={null}>
            <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/en" element={<Index />} />
              <Route path="/lakarintyg" element={<Lakarintyg />} />
              <Route path="/läkarintyg" element={<Lakarintyg />} />
              <Route path="/en/lakarintyg" element={<Lakarintyg />} />
              <Route path="/kontakt" element={<Kontakt />} />
              <Route path="/en/contact" element={<Kontakt />} />
              <Route path="/boka-tid" element={<Boka />} />
              <Route path="/en/book-appointment" element={<Boka />} />
              <Route path="/fragor-och-svar" element={<Faq />} />
              <Route path="/en/faq" element={<Faq />} />
              <Route path="/infor-besoket" element={<InforBesoket />} />
              <Route path="/en/before-visit" element={<InforBesoket />} />
              <Route path="/sa-fungerar-det" element={<SaFungerarDet />} />
              <Route path="/en/how-it-works" element={<SaFungerarDet />} />
              <Route path="/guider" element={<Guider />} />
              <Route path="/en/guides" element={<Guider />} />
              <Route path="/foretag" element={<Foretag />} />
              <Route path="/foretag/arbetsmiljo" element={<ForetagArbetsmiljo />} />
              <Route path="/foretag/drogtestning" element={<ForetagDrogtestning />} />
              <Route path="/foretag/tjanster" element={<ForetagTjanster />} />
              <Route path="/foretag/kontakt" element={<ForetagKontakt />} />
              <Route path="/foretag/boka-tid" element={<ForetagBokaTid />} />
              <Route path="/en/companies" element={<Foretag />} />
              <Route path="/en/companies/occupational-health" element={<ForetagArbetsmiljo />} />
              <Route path="/en/companies/drug-testing" element={<ForetagDrogtestning />} />
              <Route path="/en/companies/services" element={<ForetagTjanster />} />
              <Route path="/en/companies/contact" element={<ForetagKontakt />} />
              <Route path="/en/companies/book" element={<ForetagBokaTid />} />
              <Route path="/halsokontroller" element={<Halsokontroller />} />
              <Route path="/en/health-checks" element={<Halsokontroller />} />
              <Route path="/om-oss" element={<OmOss />} />
              <Route path="/en/about-us" element={<OmOss />} />
              <Route path="/integritetspolicy" element={<LegalDocument docId="privacy" />} />
              <Route path="/en/privacy-policy" element={<LegalDocument docId="privacy" />} />
              <Route path="/cookie-policy" element={<LegalDocument docId="cookies" />} />
              <Route path="/cookiepolicy" element={<Navigate to="/cookie-policy" replace />} />
              <Route path="/en/cookie-policy" element={<LegalDocument docId="cookies" />} />
              <Route path="/anvandarvillkor" element={<LegalDocument docId="terms" />} />
              <Route path="/en/terms-of-use" element={<LegalDocument docId="terms" />} />
              <Route path="/bekraftelse" element={<Bekraftelse />} />
              <Route path="/en/confirmation" element={<Bekraftelse />} />
              <Route path="/starta-arende" element={<StartaArende />} />
              <Route path="/en/start-case" element={<StartaArende />} />
              <Route path="/provtagning" element={<Provtagning />} />
              <Route path="/en/sample-collection" element={<Provtagning />} />
              <Route path="/drogtestning" element={<Drogtestning />} />
              <Route path="/drogtestning/privat" element={<DrogtestningPrivat />} />
              <Route path="/drogtestning/for-intyg" element={<DrogtestningForIntyg />} />
              <Route path="/drogtestning/salivtest" element={<Navigate to="/lakarintyg/salivtest" replace />} />
              <Route path="/drogtestning/urinprov" element={<Navigate to="/lakarintyg/urinprov" replace />} />
              <Route path="/drogtestning/boka/:slug" element={<DrogtestningBoka />} />
              <Route path="/drogtestning/forfragan" element={<DrogtestningForfragan />} />
              <Route path="/en/drug-testing" element={<Drogtestning />} />
              <Route path="/en/drug-testing/private" element={<DrogtestningPrivat />} />
              <Route path="/en/drug-testing/for-certificate" element={<DrogtestningForIntyg />} />
              <Route
                path="/en/drug-testing/saliva"
                element={<Navigate to="/en/medical-certificates/saliva" replace />}
              />
              <Route
                path="/en/drug-testing/urine"
                element={<Navigate to="/en/medical-certificates/urine" replace />}
              />
              <Route path="/en/drug-testing/book/:slug" element={<DrogtestningBoka />} />
              <Route path="/en/drug-testing/enquiry" element={<DrogtestningForfragan />} />
              <Route path="/lakarintyg/:slug" element={<CertificateDetail />} />
              <Route path="/en/medical-certificates/:slug" element={<CertificateDetail />} />
              <Route path="/guider/:category/:slug" element={<GuideDetail />} />
              <Route path="/en/guides/:category/:slug" element={<GuideDetail />} />
              {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
          <Suspense fallback={null}>
            <FindCertificateFab />
          </Suspense>
          <CookieBanner />
        </LangProvider>
      </BrowserRouter>
    </TooltipProvider>
  );
};

export default App;
